import { useMemo } from 'react'

import { Filter } from 'modules/core/components/DataTable/types'

const DISABLED_OPACITY = 0.3

interface ChartData {
  [key: string]: unknown
}

interface FilterConfig {
  enabledFilters: Filter<unknown>[]
  availableFilters: Filter<unknown>[]
  dataTableFilters: Record<string, unknown>
  scoreFilterIdentifier: string
}

interface FilterOption {
  value: unknown
}

interface FilterCategory {
  options: FilterOption[]
}

interface ParsedFilter {
  category?: FilterCategory
}

function isValidFilterStructure(obj: unknown): obj is { category: FilterCategory } {
  return Boolean(
    obj &&
    typeof obj === 'object' &&
    'category' in obj &&
    obj.category &&
    typeof obj.category === 'object' &&
    'options' in obj.category &&
    Array.isArray(obj.category.options)
  )
}

function hasValidOption(obj: unknown, index: number): obj is ParsedFilter {
  return isValidFilterStructure(obj) && Boolean(obj.category.options[index])
}

function hasScoreRangeFilter(
  filters: Record<string, unknown>
): filters is { scoreRange: { in: unknown[] } } {
  return Boolean(
    filters &&
    typeof filters === 'object' &&
    'scoreRange' in filters &&
    filters.scoreRange &&
    typeof filters.scoreRange === 'object' &&
    'in' in filters.scoreRange &&
    Array.isArray(filters.scoreRange.in)
  )
}

function getSelectedFilterAndOptionByIndex(
  index: number,
  enabledFilters: Filter<unknown>[],
  availableFilters: Filter<unknown>[],
  scoreFilterIdentifier: string
) {
  const scoreRangeFinder = (filter: Filter<unknown>) =>
    filter.filter.identifier === scoreFilterIdentifier

  const scoreFilterAvailable = availableFilters.find(scoreRangeFinder)
  const scoreFilterEnabled = enabledFilters.find(scoreRangeFinder)
  const scoreFilter = scoreFilterAvailable || scoreFilterEnabled

  let option: FilterOption | undefined

  if (hasValidOption(scoreFilter, index) && scoreFilter.category) {
    option = scoreFilter.category.options[index]
  }

  return {
    scoreFilter,
    selectedOption: option?.value ? option : undefined,
    filterAlreadyEnabled: !!scoreFilterEnabled,
  }
}

function isItemFiltered(
  index: number,
  enabledFilters: Filter<unknown>[],
  availableFilters: Filter<unknown>[],
  dataTableFilters: Record<string, unknown>,
  scoreFilterIdentifier: string
): boolean {
  const selectedScoreRanges = hasScoreRangeFilter(dataTableFilters)
    ? dataTableFilters.scoreRange.in
    : []

  if (selectedScoreRanges.length === 0) {
    return false
  }

  const { selectedOption } = getSelectedFilterAndOptionByIndex(
    index + 1, // Offset because index 0 represents "no data" option
    enabledFilters,
    availableFilters,
    scoreFilterIdentifier
  )

  if (!selectedOption) {
    return false
  }

  return !selectedScoreRanges.includes(selectedOption.value)
}

export function useBarChartFilters<T extends ChartData>(data: T[], config: FilterConfig): T[] {
  const { enabledFilters, availableFilters, dataTableFilters, scoreFilterIdentifier } = config

  return useMemo(() => {
    return data.map((item, index) => {
      const isFiltered = isItemFiltered(
        index,
        enabledFilters,
        availableFilters,
        dataTableFilters,
        scoreFilterIdentifier
      )

      return {
        ...item,
        __opacity: isFiltered ? DISABLED_OPACITY : 1.0,
      }
    })
  }, [data, enabledFilters, availableFilters, dataTableFilters, scoreFilterIdentifier])
}
